import { supabase } from '../supabase';
import { sortDivisions } from '../utils/divisionDisplay';

// ── Programs ───────────────────────────────────────────────────────────

/** Phase C.1 — the only live Program-creation path. create_program_with_
 * admin() (020) was revoked from authenticated in 025 once the Owner
 * model landed (it never created a program_owners row) and was never
 * replaced until now — see migration 036's own header for the full
 * "why this was a real, live gap" explanation. Creates the Program and
 * the caller's Owner row atomically; auto-generates a unique slug from
 * the name server-side. */
export async function createProgramWithOwner({ name, stateCode, countyId, city }) {
  const { data, error } = await supabase.rpc('create_program_with_owner', {
    p_name: name,
    p_state_code: stateCode || null,
    p_county_id: countyId || null,
    p_city: city || null,
  });
  if (error) throw error;
  return data;
}

/** Phase C.2 — whether the CALLER holds Program Owner/Admin authority for
 * this specific Program (025's has_program_management_authority(), same
 * RPC ManageProgramPage.js's own authority checks already rely on).
 * Granted to `authenticated` only (025) — never call this while signed
 * out; public pages must only invoke it when a currentUser exists (it's
 * used to decide whether to show that visitor a "Manage Program" link on
 * an otherwise fully public page, never to gate what the page itself
 * renders for anyone else). */
export async function hasProgramManagementAuthority(programId) {
  const { data, error } = await supabase.rpc('has_program_management_authority', { p_program_id: programId });
  if (error) throw error;
  return Boolean(data);
}

/** Programs the caller actively administers, via list_my_programs() (023)
 * — a purpose-built RPC, not a raw multi-table client query, same
 * reasoning as listTeamStaff()/listMyAthletes() elsewhere in this
 * project. Includes each Program's primary service area and a live
 * Season Team count. */
export async function listMyPrograms() {
  const { data, error } = await supabase.rpc('list_my_programs');
  if (error) throw error;
  return data;
}

export async function getProgram(programId) {
  const { data, error } = await supabase.from('programs').select('*').eq('id', programId).single();
  if (error) throw error;
  return data;
}

/** Phase C.2b — batched Program-id -> slug lookup, for My Programs' own
 * card list ("View Public Page" needs each Program's slug, but list_my_
 * program_memberships()/list_my_programs() (023/025) were never shaped
 * to return it — reshaping either via CREATE OR REPLACE ADD COLUMN is
 * not possible for a RETURNS TABLE function, the same PostgreSQL
 * limitation this schema has hit and documented repeatedly since 016).
 * Deliberately NOT a new RPC/migration — `programs.slug` is already
 * plainly readable by any authenticated user under the existing
 * "authenticated users view programs" policy (020), the exact same
 * policy getProgram() above already relies on for the single-Program
 * case. Returns a plain { [programId]: slug } map; a Program with no
 * slug is simply absent from the object (falsy lookup), never an error. */
export async function listProgramSlugsByIds(programIds) {
  const ids = (programIds || []).filter(Boolean);
  if (ids.length === 0) return {};
  const { data, error } = await supabase.from('programs').select('id, slug').in('id', ids);
  if (error) throw error;
  const bySlug = {};
  (data || []).forEach(p => { if (p.slug) bySlug[p.id] = p.slug; });
  return bySlug;
}

/** Owner-aware successor to listMyPrograms() (023) — every Program where
 * the caller holds Owner OR Program Admin authority, each row carrying
 * viewer_role: 'owner' | 'admin' (025's list_my_program_memberships()).
 * listMyPrograms() cannot distinguish the two tiers and must not be used
 * anywhere that displays or acts on authority — use this instead. Kept
 * as a SEPARATE function, not a rewrite of listMyPrograms() in place,
 * because PostgreSQL rejects adding an output column to an existing
 * RETURNS TABLE function via CREATE OR REPLACE (025's own migration
 * note) — the two RPCs are genuinely different objects. listMyPrograms()
 * itself is left in place, unused by any Program-authority-aware page. */
export async function listMyProgramMemberships() {
  const { data, error } = await supabase.rpc('list_my_program_memberships');
  if (error) throw error;
  return data;
}

/** The active Owner's identity for a Program (025's get_program_owner())
 * — callable by anyone with management authority (Owner or Program
 * Admin), or platform admin; resolved server-side since profiles' own
 * RLS would otherwise hide it from anyone but the owner themselves.
 * Returns a single object (never an array) since program_owners
 * guarantees at most one active row per program — callers never need to
 * unwrap a result set for what is always logically one owner or none. */
export async function getProgramOwner(programId) {
  const { data, error } = await supabase.rpc('get_program_owner', { p_program_id: programId });
  if (error) throw error;
  return data?.[0] ?? null;
}

// ── Season Teams belonging to a Program ─────────────────────────────────

/** Every Season Team under this Program, newest season first — safe only
 * because 023 adds a Program-Admin-scoped SELECT policy to teams; before
 * that, a Program Admin could only see teams they personally held a
 * team_staff row on. */
export async function listProgramSeasonTeams(programId) {
  const { data, error } = await supabase
    .from('teams')
    .select('*')
    .eq('program_id', programId)
    .order('season', { ascending: false })
    .order('team_name', { ascending: true });
  if (error) throw error;
  return data;
}

/** The only Program-gated Season Team creation path (022) — atomic,
 * server-side: creates the team under this Program and the creating
 * Program Admin's team_admin bridge row together. Never creates a
 * head_coach row — see create_season_team_in_program()'s own comments. */
// ── Program Admins (024) ──────────────────────────────────────────────

/** Every program_admins row for this Program (active AND revoked — same
 * "return full history" convention as listTeamStaff()), with names/
 * emails via the list_program_admins() RPC — a direct client join to
 * profiles would come back null for every admin but the caller. */
export async function listProgramAdmins(programId) {
  const { data, error } = await supabase.rpc('list_program_admins', { p_program_id: programId });
  if (error) throw error;
  return data;
}

export async function addProgramAdmin({ programId, email }) {
  const { data, error } = await supabase.rpc('add_program_admin', {
    p_program_id: programId,
    p_email: email,
  });
  if (error) throw error;
  return data;
}

/** Self-scoped only — revokes the CALLER's own program_admins row for
 * this Program. Never touches any team_staff relationship the caller
 * separately holds (Team Admin/Head Coach/Assistant Coach on any Season
 * Team) — see relinquish_program_admin()'s own comments (024). As of
 * 025, no longer blocked by a minimum-admin-count safeguard — a Program
 * may validly have zero delegated Program Admins as long as it has its
 * one Owner, so this can always succeed for an ordinary active admin. */
export async function relinquishProgramAdmin(programId) {
  const { data, error } = await supabase.rpc('relinquish_program_admin', { p_program_id: programId });
  if (error) throw error;
  return data;
}

/** Owner-only (025) — revokes a SPECIFIC delegated Program Admin's row,
 * identified by their program_admins row id (not their user id) — same
 * shape as revokeTeamStaff(). Never touches team_staff. No last-admin
 * restriction applies; a Program may validly end up with zero delegated
 * Admins. */
export async function removeProgramAdmin(programAdminId) {
  const { data, error } = await supabase.rpc('remove_program_admin', { p_program_admin_id: programAdminId });
  if (error) throw error;
  return data;
}

/** Owner-only (025) — atomically transfers Program ownership to an
 * existing FFN account. retainOutgoingAsAdmin defaults false and is
 * never automatic — an explicit, single-transaction opt-in the outgoing
 * Owner chooses at the moment of transfer. Never touches team_staff for
 * either party. */
export async function transferProgramOwnership({ programId, targetEmail, retainOutgoingAsAdmin = false }) {
  const { data, error } = await supabase.rpc('transfer_program_ownership', {
    p_program_id: programId,
    p_target_email: targetEmail,
    p_retain_outgoing_as_admin: retainOutgoingAsAdmin,
  });
  if (error) throw error;
  return data;
}

export async function createSeasonTeamInProgram({
  programId, teamName, city, state, zipCode, season, gradeAgeGroup, organizationName,
}) {
  const { data, error } = await supabase.rpc('create_season_team_in_program', {
    p_program_id: programId,
    p_team_name: teamName,
    p_city: city,
    p_state: state,
    p_zip_code: zipCode,
    p_season: season,
    p_grade_age_group: gradeAgeGroup,
    p_organization_name: organizationName || null,
  });
  if (error) throw error;
  return data;
}

/** Successor to createSeasonTeamInProgram() above (028) — atomically
 * writes structured classification (classificationType/Values) and
 * football format (playersPerSide) alongside team creation, via the new
 * create_season_team_in_program_with_classification() RPC. This is the
 * ONLY creation path CreateSeasonTeamPage.js uses now. Kept as a separate
 * function/RPC rather than a reshape of createSeasonTeamInProgram() above
 * — see migration 028's own comments for why CREATE OR REPLACE can't
 * extend that function's parameter list in place. gradeAgeGroup here is
 * the auto-computed legacy-fallback display string (from utils/
 * teamClassification.js's formatter), not a value the user types
 * directly. */
export async function createSeasonTeamInProgramWithClassification({
  programId, teamName, city, state, zipCode, season, gradeAgeGroup,
  classificationType, classificationValues, playersPerSide, organizationName,
}) {
  const { data, error } = await supabase.rpc('create_season_team_in_program_with_classification', {
    p_program_id: programId,
    p_team_name: teamName,
    p_city: city,
    p_state: state,
    p_zip_code: zipCode,
    p_season: season,
    p_grade_age_group: gradeAgeGroup,
    p_classification_type: classificationType,
    p_classification_values: classificationValues,
    p_players_per_side: playersPerSide,
    p_organization_name: organizationName || null,
  });
  if (error) throw error;
  return data;
}

/** Program management authority only (Owner or Admin, or platform admin) —
 * update_team_classification() (028). Allowed only while the team's
 * season_status is 'setup' or 'preseason'; rejected server-side once it's
 * active/closeout/ended, regardless of what the frontend shows. Never
 * usable by team_admin/head_coach/assistant_coach alone.
 *
 * gradeAgeGroup should be the SAME formatted display string creation
 * uses (utils/teamClassification.js's formatTeamClassification()) — the
 * RPC writes it into the legacy grade_age_group column atomically
 * alongside the structured columns, so guardian/join-code preview
 * surfaces (which still read grade_age_group directly) never go stale
 * after a Settings edit. Pass null/omit when there's nothing new to show
 * (e.g. only playersPerSide changed) — the RPC leaves the existing
 * grade_age_group untouched rather than blanking it.
 *
 * Superseded by updateTeamClassificationAndFootballType() below (029) —
 * kept in place, unused by any page, same dormant-legacy-function
 * discipline as createSeasonTeamInProgram() above. */
export async function updateTeamClassification({ teamId, classificationType, classificationValues, playersPerSide, gradeAgeGroup }) {
  const { data, error } = await supabase.rpc('update_team_classification', {
    p_team_id: teamId,
    p_classification_type: classificationType,
    p_classification_values: classificationValues,
    p_players_per_side: playersPerSide,
    p_grade_age_group: gradeAgeGroup || null,
  });
  if (error) throw error;
  return data;
}

/** Successor to createSeasonTeamInProgramWithClassification() above (029)
 * — atomically writes football_type alongside everything that function
 * already wrote, via the new create_season_team_in_program_with_
 * football_type() RPC. This is the ONLY creation path
 * CreateSeasonTeamPage.js uses now. Kept as a separate function/RPC
 * rather than a reshape of the 028 function — see migration 029's own
 * comments for why CREATE OR REPLACE can't extend that function's
 * parameter list in place. */
export async function createSeasonTeamInProgramWithFootballType({
  programId, teamName, city, state, zipCode, season, gradeAgeGroup,
  classificationType, classificationValues, playersPerSide, footballType, organizationName,
}) {
  const { data, error } = await supabase.rpc('create_season_team_in_program_with_football_type', {
    p_program_id: programId,
    p_team_name: teamName,
    p_city: city,
    p_state: state,
    p_zip_code: zipCode,
    p_season: season,
    p_grade_age_group: gradeAgeGroup,
    p_classification_type: classificationType,
    p_classification_values: classificationValues,
    p_players_per_side: playersPerSide,
    p_football_type: footballType,
    p_organization_name: organizationName || null,
  });
  if (error) throw error;
  return data;
}

/** Successor to updateTeamClassification() above (029) — Program
 * management authority only, same lifecycle enforcement (setup/preseason
 * only), via update_team_classification_and_football_type(). Updates
 * classification, football format, football_type, and the grade_
 * age_group mirror together in one call — this is now the ONE Team-
 * definition edit RPC TeamSettingsPage.js uses. footballType follows the
 * same "cannot clear once set" rule as classificationType server-side. */
export async function updateTeamClassificationAndFootballType({
  teamId, classificationType, classificationValues, playersPerSide, footballType, gradeAgeGroup,
}) {
  const { data, error } = await supabase.rpc('update_team_classification_and_football_type', {
    p_team_id: teamId,
    p_classification_type: classificationType,
    p_classification_values: classificationValues,
    p_players_per_side: playersPerSide,
    p_football_type: footballType,
    p_grade_age_group: gradeAgeGroup || null,
  });
  if (error) throw error;
  return data;
}

/** Program management authority only (Owner or Admin, or platform admin) —
 * archive_team()/reactivate_team() (028). ADDITIONAL paths to teams.status
 * alongside the pre-existing team_admin plain-update toggle (teamService.
 * updateTeam(), still unchanged) — these never replace that legacy path,
 * they extend the same capability to Program Owner/Admin who may not
 * personally hold team_admin. Permitted regardless of season_status;
 * never touches season_status or Program status. */
export async function archiveTeam(teamId) {
  const { data, error } = await supabase.rpc('archive_team', { p_team_id: teamId });
  if (error) throw error;
  return data;
}

export async function reactivateTeam(teamId) {
  const { data, error } = await supabase.rpc('reactivate_team', { p_team_id: teamId });
  if (error) throw error;
  return data;
}

// ── Setup Team lifecycle (027) ───────────────────────────────────────────
// As of 027, create_season_team_in_program() above starts a new Season
// Team at season_status = 'setup', not 'preseason' — the functions below
// are what move it (and, narrowly, what removes a genuinely-still-empty
// one created by mistake).

/** Program management authority (Owner or Program Admin) for a SPECIFIC
 * team, resolved server-side via 025's has_program_management_authority_
 * for_team() — called directly as a boolean-returning RPC (it's already
 * granted to authenticated for exactly this kind of use, not only as an
 * internal helper other functions call). Used to decide whether to show
 * Setup-lifecycle controls on a team's own page; the RPCs below
 * independently re-derive and enforce this same check server-side
 * regardless of what this returns. */
export async function hasProgramManagementAuthorityForTeam(teamId) {
  const { data, error } = await supabase.rpc('has_program_management_authority_for_team', { p_team_id: teamId });
  if (error) throw error;
  return Boolean(data);
}

/** Program Owner specifically (not Admin) for a SPECIFIC team, via 025's
 * is_program_owner_for_team() — used only to decide whether to show the
 * Owner-exclusive "Delete Setup Team" control. */
export async function isProgramOwnerForTeam(teamId) {
  const { data, error } = await supabase.rpc('is_program_owner_for_team', { p_team_id: teamId });
  if (error) throw error;
  return Boolean(data);
}

/** SETUP -> PRESEASON only (027's begin_team_preseason()) — Program
 * management authority only; no team_staff role grants this in this
 * phase. Rejected server-side if the team is not currently in Setup. */
export async function beginTeamPreseason(teamId) {
  const { data, error } = await supabase.rpc('begin_team_preseason', { p_team_id: teamId });
  if (error) throw error;
  return data;
}

/** Owner-only (027's delete_setup_team()) — permanently deletes a Season
 * Team, allowed ONLY while it is still in Setup AND has no meaningful
 * team_staff/roster/join-code/roster-request/athlete-connection activity
 * beyond its own creator's bridge row. Never available once a team enters
 * Preseason. This is a real, irreversible delete — not a status change —
 * unlike every other team-lifecycle action in this app. */
export async function deleteSetupTeam(teamId) {
  const { error } = await supabase.rpc('delete_setup_team', { p_team_id: teamId });
  if (error) throw error;
}

// ── Seasons / Divisions (Phase C.1, migration 036) ──────────────────────
// seasons/divisions carry the SAME broad "authenticated users view"
// RLS policy programs itself already has (036) — a plain client-side
// select is safe and sufficient for reads; only creation/edit go through
// a SECURITY DEFINER RPC (same "no direct INSERT policy" convention as
// programs/teams throughout this schema).

/** Every Season for this Program, newest year first. */
export async function listProgramSeasons(programId) {
  const { data, error } = await supabase
    .from('seasons')
    .select('*')
    .eq('program_id', programId)
    .order('year', { ascending: false });
  if (error) throw error;
  return data;
}

export async function createSeason({ programId, year, name, startDate, endDate }) {
  const { data, error } = await supabase.rpc('create_season', {
    p_program_id: programId,
    p_year: year,
    p_name: name || null,
    p_start_date: startDate || null,
    p_end_date: endDate || null,
  });
  if (error) throw error;
  return data;
}

/** Every Division for this Season, in canonical grade order (Phase
 * C.3.1 — see utils/divisionDisplay.js's own sortDivisions() for why a
 * raw display_order isn't sufficient on its own). The underlying query
 * still orders by display_order at the database level (a reasonable
 * stable base ordering); this re-sorts client-side using grade_min/
 * grade_max, the same reusable rule the public Division list uses. */
export async function listSeasonDivisions(seasonId) {
  const { data, error } = await supabase
    .from('divisions')
    .select('*')
    .eq('season_id', seasonId)
    .order('display_order', { ascending: true });
  if (error) throw error;
  return sortDivisions(data);
}

/** Phase C.4a — one Division by id, for the private Division Hub's own
 * header. Plain client select, same "authenticated users view divisions"
 * broad RLS policy (036) listSeasonDivisions() already relies on.
 *
 * Browser-found fix (Division Hub Season context) — this used to be a
 * bare `select('*')` with no Season join at all, so `division.season_name`
 * was always undefined here (DivisionHub silently skipped rendering the
 * Season line, gracefully but wrongly — ambiguous the moment a second
 * Season exists). Embeds the Season's own `name` via the existing
 * divisions.season_id -> seasons.id relationship — same field
 * getPublicDivision() (038) already returns, so the authenticated and
 * public Hub show the exact same "<name> Season" text. */
export async function getDivision(divisionId) {
  const { data, error } = await supabase.from('divisions').select('*, seasons(name)').eq('id', divisionId).single();
  if (error) throw error;
  const { seasons, ...division } = data;
  return { ...division, season_name: seasons?.name || null };
}

export async function createDivision({
  seasonId, name, competitionType, gradeMin, gradeMax, classification, playersPerSide, displayOrder,
}) {
  const { data, error } = await supabase.rpc('create_division', {
    p_season_id: seasonId,
    p_name: name,
    p_competition_type: competitionType || 'other',
    p_grade_min: gradeMin ?? null,
    p_grade_max: gradeMax ?? null,
    p_classification: classification || null,
    p_players_per_side: playersPerSide ?? null,
    p_display_order: displayOrder ?? null,
  });
  if (error) throw error;
  return data;
}

export async function updateDivision({
  divisionId, name, competitionType, gradeMin, gradeMax, classification, playersPerSide, displayOrder, status,
}) {
  const { data, error } = await supabase.rpc('update_division', {
    p_division_id: divisionId,
    p_name: name,
    p_competition_type: competitionType || 'other',
    p_grade_min: gradeMin ?? null,
    p_grade_max: gradeMax ?? null,
    p_classification: classification || null,
    p_players_per_side: playersPerSide ?? null,
    p_display_order: displayOrder ?? null,
    p_status: status || 'active',
  });
  if (error) throw error;
  return data;
}

/** The lean Team-creation path for a Team that belongs to a Division from
 * the start — no classification/football-type step (see migration 036's
 * own header). A Program Admin who also wants structured classification
 * can call updateTeamClassificationAndFootballType() afterward — the
 * same already-live RPC TeamSettingsPage.js uses. */
export async function createTeamInDivision({ divisionId, teamName, city, state, zipCode, organizationName }) {
  const { data, error } = await supabase.rpc('create_team_in_division', {
    p_division_id: divisionId,
    p_team_name: teamName,
    p_city: city,
    p_state: state,
    p_zip_code: zipCode,
    p_organization_name: organizationName || null,
  });
  if (error) throw error;
  return data;
}

/** Re-parents an EXISTING Program Team into a Division (or clears it,
 * passing divisionId null) — how a Team created before Divisions existed
 * (or via any other still-live creation RPC) joins the hierarchy. Used
 * ONLY for that null -> value "Unassigned Teams" case (036) — see
 * moveTeamToDivision() below for moving an already-assigned Team. */
export async function assignTeamToDivision({ teamId, divisionId }) {
  const { data, error } = await supabase.rpc('assign_team_to_division', {
    p_team_id: teamId,
    p_division_id: divisionId,
  });
  if (error) throw error;
  return data;
}

/** Phase C.4a — moves an ALREADY-assigned Team to a different Division
 * (042's move_team_to_division()). Deliberately a SEPARATE function from
 * assignTeamToDivision() above, not a reuse of it — that RPC has no
 * awareness of team_matchups and would silently break the Division
 * public page's read-time invariant if used to move a Team with
 * existing internal competition history. This one blocks the move
 * outright (a clear, named error, not a raw DB failure) whenever the
 * Team participates in ANY team_matchups row, in any status — see 042's
 * own header for the full reasoning. Never falls back to
 * assignTeamToDivision() on failure. */
export async function moveTeamToDivision({ teamId, divisionId }) {
  const { data, error } = await supabase.rpc('move_team_to_division', {
    p_team_id: teamId,
    p_division_id: divisionId,
  });
  if (error) throw error;
  return data;
}

/** Program management authority only — whether this Team has a public
 * page and, optionally, its slug. See migration 036's own header for the
 * product rule this encodes: a Team existing does not require one. */
export async function updateTeamPublicPageSettings({ teamId, publicPageEnabled, slug }) {
  const { data, error } = await supabase.rpc('update_team_public_page_settings', {
    p_team_id: teamId,
    p_public_page_enabled: publicPageEnabled,
    p_slug: slug || null,
  });
  if (error) throw error;
  return data;
}
